<script setup>
import { ref, onMounted, computed, watch, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import {bbox} from '@turf/turf'

import { toast } from '@/components/ui/toast'
const FeaturesList = defineAsyncComponent(() => import('@/components/features/FeaturesList.vue'))
import { Spinner } from '@/components/ui/spinner'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'

const total = ref(0)
const page = ref(1)
const limit = ref(15)
const offset = computed(() => (page.value - 1) * limit.value)

const store = useStore()
const member = computed(() => store.getters['user/getMember'])
const params = computed(() => store.getters['search/getParams'])
const totalInFocus = computed(() => store.getters['geo/getTotalOnMap'])
const isLoggedIn = computed(() => store.getters.getIsLoggedIn)
const map = computed(() => store.getters['geo/getMap'])
const features = computed(() => store.getters['geo/getFeedFeatures'])

// Filter states
const viewportOnly = ref(localStorage.getItem('viewportOnly') === 'true')
const staticOnly = ref(localStorage.getItem('staticOnly') === 'true')
const publicOnly = ref(localStorage.getItem('publicOnly') === 'true')
const dataFilter = ref(localStorage.getItem('dataFilter') || 'all') // 'all', 'private'
const isFetchingBoundary = ref(false)

const router = useRouter()
const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))

const featuresLength = computed(() => features.value.features?.length)
const isFeatureCreated = computed(() => store.getters['geo/getIsFeatureCreated'])
const isLoading = ref(false)

const fetchFeatures = async () => {
  const currentProjectId = projectId.value
  if (!currentProjectId) return

  isLoading.value = true
  try {
    const paramsMap = params.value.reduce((acc, param) => {
      acc[param.key] = param.value;
      return acc
    }, {})

    const payload = {
      has_resources: paramsMap['has_resources'],
      start_date: paramsMap['start_date'],
      end_date: paramsMap['end_date']
    }

    if (dataFilter.value === 'private') {
      payload.shared = false
      payload.member_id = member.value?.member_id
    }

    if (staticOnly.value) {
      payload.is_static = true
    }

    if (publicOnly.value) {
      payload.shared = true
    }

    const fullPayload = {
      ...payload,
      isMeta: true,
      project_id: currentProjectId,
      south: viewportOnly.value ? paramsMap['south'] : null,
      north: viewportOnly.value ? paramsMap['north'] : null,
      east: viewportOnly.value ? paramsMap['east'] : null,
      west: viewportOnly.value ? paramsMap['west'] : null,
      limit: limit.value,
      offset: offset.value
    }

    await store.dispatch('geo/fetchFeatures', fullPayload)

    const response_total = await API({
      method: 'GET',
      url: `${HOST}/geo/features/${currentProjectId}/total`,
      params: payload,
    })

    total.value = response_total.data
  } catch (e) {
    console.log(e)
    if (e.response?.status === 403) {
      toast({title: `You don't have access to this data`})
    } else if (e.response?.status === 404) {
      toast({title: `Data not found`})
    } else {
      throw e
    }
  }

  isLoading.value = false
}

onMounted(async () => {
  if (isLoggedIn.value) {
    await fetchFeatures()
  }
})

watch(
  () => isLoggedIn.value,
  async (newValue, oldValue) => {
    if (newValue && !oldValue) {
      await fetchFeatures()
    }
  }
)

watch(
  () => projectId.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      await fetchFeatures()
    }
  }
)

watch(
  () => params.value,
  async (newValue, oldValue) => {
    if (!oldValue) return
    await fetchFeatures()
  },
  { deep: true }
)

watch(
  () => page.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      await fetchFeatures()
    }
  }
)

watch(
  () => isFeatureCreated.value,
  async (value) => {
    if (value) {
      await fetchFeatures()
      store.commit('geo/SET_FEATURE_CREATED', false)
    }
  }
)

watch(
  () => dataFilter.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      page.value = 1 // Reset to first page when filter changes
      await fetchFeatures()
    }
  }
)

watch(
  () => staticOnly.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      page.value = 1 // Reset to first page when filter changes
      await fetchFeatures()
    }
  }
)

watch(
  () => publicOnly.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      page.value = 1 // Reset to first page when filter changes
      await fetchFeatures()
    }
  }
)

const paginationUpdated = async (pageCount) => {
  page.value = pageCount
}

const focusOnFilteredData = async () => {
  isFetchingBoundary.value = true
  const boundary = await store.dispatch('geo/fetchProjectBoundary', projectId.value)

  if (!boundary) {
    isFetchingBoundary.value = false
    return
  }

  const geometry = boundary.geometry

  if (!geometry || !Array.isArray(geometry.coordinates) || geometry.coordinates.length === 0) {
    isFetchingBoundary.value = false
  }

  const bounds = bbox(geometry)
  if (!bounds || bounds.length !== 4) {
    isFetchingBoundary.value = false
  }
  map.value.fitBounds(bounds)
  isFetchingBoundary.value = false
}

const setDataFilter = async (filter) => {
  dataFilter.value = filter
  localStorage.setItem('dataFilter', filter)
}

const toggleStaticOnly = async (value) => {
  staticOnly.value = value
  localStorage.setItem('staticOnly', value)
}

const togglePublicOnly = async (value) => {
  publicOnly.value = value
  localStorage.setItem('publicOnly', value)
}

const toggleViewportOnly = async (value) => {
  isLoading.value = true
  viewportOnly.value = value
  localStorage.setItem('viewportOnly', value)
  await fetchFeatures()

  if (!value) {
    await focusOnFilteredData()
  }
  isLoading.value = false
}
</script>

<template>
  <div class="px-1 py-1 sticky top-0 bg-gray-100 space-y-2">
    <!-- Data Filter Buttons -->
    <div class="flex items-center space-x-1">
      <Button
        @click="setDataFilter('all')"
        class="w-full font-semibold"
        :class="{'text-primary shadow-xs hover:text-primary hover:bg-white': dataFilter === 'all'}"
        :variant="dataFilter === 'all' ? 'white' : 'ghost'"
        size="sm"
      >
        All Data
      </Button>
      <Button
        @click="setDataFilter('private')"
        class="w-full font-semibold"
        :class="{'text-primary shadow-xs hover:text-primary hover:bg-white': dataFilter === 'private'}"
        :variant="dataFilter === 'private' ? 'white' : 'ghost'"
        size="sm"
      >
        My Data
      </Button>
    </div>
  </div>

  <div class="px-3 py-2 border-b border-gray-200 space-y-2">
    <div>
      <div class="flex items-center">
        <Switch
          :model-value="viewportOnly"
          @update:model-value="toggleViewportOnly"
        />
        <span class="text-sm font-medium ml-2">Show viewport data only</span>
      </div>

      <!-- <div class="flex items-center justify-between px-2 py-1 bg-white rounded border">
        <span class="text-sm font-medium">Static only</span>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            :checked="staticOnly"
            @change="toggleStaticOnly($event.target.checked)"
            class="sr-only peer"
          >
          <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      <div class="flex items-center justify-between px-2 py-1 bg-white rounded border">
        <span class="text-sm font-medium">Public only</span>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            :checked="publicOnly"
            @change="togglePublicOnly($event.target.checked)"
            class="sr-only peer"
          >
          <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div> -->
    </div>

    <div class="flex items-center justify-between text-gray-600">
      <span>Total: {{ total }}</span>
      <span v-if="viewportOnly">In View: {{ totalInFocus }}</span>
    </div>
  </div>

  <div v-if="featuresLength > 0">
    <!-- Features List with Selection -->
    <div class="relative">
      <FeaturesList v-if="!isLoading" :features="features.features" />
    </div>

    <div v-if="isLoading" class="py-7 flex items-center justify-center">
      <Spinner class="w-12 h-12" />
    </div>

    <div v-if="(viewportOnly ? totalInFocus : total) > limit && !isLoading" class="p-3">
      <Pagination
        v-slot="{ page }"
        :total="viewportOnly ? totalInFocus : total"
        :sibling-count="1"
        show-edges
        :default-page="page"
        :items-per-page="limit"
        @update:page="paginationUpdated"
      >
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
          <PaginationFirst />
          <PaginationPrev />

          <template v-for="(item, index) in items">
            <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === page ? 'default' : 'outline'">
                {{ item.value }}
              </Button>
            </PaginationListItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>

          <PaginationNext />
          <PaginationLast />
        </PaginationList>
      </Pagination>
    </div>
  </div>
  <div v-else class="p-3 w-full h-full flex items-center justify-center">
    <div v-if="!isLoading && featuresLength === 0" class="mt-0 text-center">
      <div v-if="viewportOnly">
        <h3 class="text-base font-bold">No data in view</h3>
        <p class="text-gray-500">Try panning or zooming out to find data.</p>
        <Button
          @click="toggleViewportOnly(false)"
          class="text-primary mt-3"
          variant="secondary"
          size="sm"
        >
          Switch to all data
        </Button>
      </div>
      <div v-else>
        <h3 class="text-base font-bold">No data found</h3>
        <p class="text-gray-500">
          Adjust filters or select a different project.
        </p>
      </div>
    </div>
  </div>
</template>
